/**
 * Rides booked for later, and the ones that are running out of time.
 *
 * Its own card rather than rows in the queue, because these are a different kind of work.
 * Nothing is wrong with a booking for Tuesday, and mixing them into the live queue either
 * buries tonight's problems under next week's or makes Tuesday look like one.
 *
 * The exception is a booking that has run out of search time with nobody going. That is
 * tonight's problem, it is flagged, and it sorts to the top — an airport run at four in the
 * morning with no driver is the case this whole feature exists for.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatcherHub } from '../realtime/useDispatcherHub';
import { useTenantFormats } from '../shell/formats';
import { Card, CardHeader, Count, Pill, ScrollList, Sub } from '../ui';
import { dispatchApi, timeSpanToMinutes, type PreBooking } from './api';

export function PreBookings({ className }: { className?: string }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [bookings, setBookings] = useState<PreBooking[] | null>(null);

  const refresh = useCallback(async () => {
    try {
      setBookings(await dispatchApi.preBookings());
    } catch {
      // A failure here must not take the queue down with it. An empty list reads as "none
      // upcoming", which is wrong but harmless; an exception would blank the dispatch screen
      // over a card nobody was looking at.
      setBookings([]);
    }
  }, []);

  // The same signal the queue listens to. A pre-booking becomes an ordinary order the moment
  // it falls due, and one that gets flagged publishes a change too.
  useDispatcherHub({ onOrderChanged: refresh, onReconnected: refresh });

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void refresh();
  }, [refresh]);

  // Nothing at all is the common case for most firms most of the time, and an empty card
  // taking up a third of a row would be worse than no card.
  if (bookings === null || bookings.length === 0) return null;

  return (
    <Card className={className}>
      <CardHeader title={t('prebookings.title')}>
        <Count>{bookings.length}</Count>
      </CardHeader>

      {/* One of three cards on a row that is meant to be short; a firm can have a page of
          bookings for tomorrow. */}
      <ScrollList>
        {bookings.map((booking) => (
          <li
            key={booking.orderId}
            className="flex items-center justify-between gap-2 px-3.5 py-2 border-b border-rule last:border-b-0"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                {booking.atRisk && (
                  <Pill tone="danger" soft title={t('prebookings.atRiskHelp')}>
                    {t('prebookings.atRisk')}
                  </Pill>
                )}
                <span className="font-mono font-semibold tracking-[0.03em]">
                  {booking.reference}
                </span>
                <span className="truncate">{booking.pickupAddress}</span>
              </div>

              <Sub block>
                {formats.moment(booking.scheduledFor)}
                {booking.customerName !== null && ` · ${booking.customerName}`}
              </Sub>
            </div>

            <span className="text-ink-3 tabular-nums shrink-0">
              {t('prebookings.dueIn', {
                minutes: Math.max(0, Math.round(timeSpanToMinutes(booking.dueIn))),
              })}
            </span>
          </li>
        ))}
      </ScrollList>
    </Card>
  );
}
